import { Link } from 'react-router-dom'

import { FxAlert, FxButton, FxIcon, FxPanel, FxSearchField, FxSegmentedTabs, FxSpinner } from '@/components/fx'
import { ArmNotice, ArmPicker } from './records/ArmPicker'
import { DELIVERED } from './records/armOptions'
import { RecordsExport } from './records/RecordsExport'
import { RecordsTable } from './records/RecordsTable'
import { useRecords } from './records/useRecords'
import { useCockpitContext } from './useCockpit'

/**
 * The cockpit's "Datensätze" tab: every analysed document as a row, one
 * column per shared template field.
 *
 * The columns come from `GET /v1/templates/:id/fields`, never from a
 * document's own result. A template with layout variants has fields only some
 * pages carry, so a table keyed off whatever the first document contained
 * would change shape as you page — and the export, which keys its columns the
 * same way, would disagree with the screen.
 *
 * Everything server-side lives in `records/useRecords.ts`; this file is the
 * screen.
 */
export function RecordsTab() {
  const cockpit = useCockpitContext()
  const templateId = cockpit.templateId
  const threshold = cockpit.template?.confidenceThreshold ?? 0.8

  const records = useRecords(templateId, { threshold, counts: cockpit.counts })

  const total = records.totalRecords ?? records.loadedCount
  const flaggedTotal = records.needsReviewTotal
  const armOption = records.armOptions.find((option) => option.value === records.arm)
  const narrowed = records.needsReviewOnly || records.query.trim() !== ''

  return (
    <FxPanel
      title={`${total} ${total === 1 ? 'Datensatz' : 'Datensätze'}`}
      meta={
        cockpit.template?.llmConsent ? (
          <span className="inline-flex items-center gap-2 rounded-sm bg-primary-subtle px-2 py-[2px] text-small">
            <FxIcon name="auto_awesome" size={16} className="text-primary" />
            KI-Zweitmeinung freigegeben
          </span>
        ) : undefined
      }
      action={
        <RecordsExport
          templateId={templateId}
          armRequest={records.armRequest}
          visibleDocumentIds={records.rows.map((row) => row.documentId)}
          narrowed={narrowed}
        />
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <FxSegmentedTabs
          aria-label="Datensätze filtern"
          items={[
            { value: 'alle', label: `Alle${records.totalRecords != null ? ` (${records.totalRecords})` : ''}` },
            {
              value: 'unklar',
              label: `Nur unklare${flaggedTotal != null ? ` (${flaggedTotal})` : ''}`,
            },
          ]}
          active={records.needsReviewOnly ? 'unklar' : 'alle'}
          onSelect={(value) => records.setNeedsReviewOnly(value === 'unklar')}
          className="gap-3"
        />
        <span className="grow" />
        <ArmPicker
          options={records.armOptions}
          value={records.arm}
          onChange={records.setArm}
          disabled={records.isArmLoading}
        />
        {/* The label says "geladen" on purpose: `GET /documents` has no `q`,
            so this filters the rows in hand and nothing else. A box that
            promises a search of the whole template and misses everything on
            the pages after this one is worse than none. */}
        <FxSearchField
          placeholder="In geladenen Datensätzen suchen"
          value={records.query}
          onChange={(event) => records.setQuery(event.target.value)}
        />
      </div>

      <ArmNotice
        option={armOption}
        coverage={records.armCoverage}
        empty={records.armEmpty}
        onReset={() => records.setArm(DELIVERED)}
      />

      {cockpit.busy && (
        <p className="mb-5 flex flex-wrap items-center gap-3 text-small text-text-muted">
          Eine Analyse läuft. Neue Datensätze erscheinen nach dem Aktualisieren.
          <FxButton variant="outlined" onClick={records.refetch}>
            Aktualisieren
          </FxButton>
        </p>
      )}

      <RecordsBody records={records} templateId={templateId} />
    </FxPanel>
  )
}

function RecordsBody({
  records,
  templateId,
}: {
  records: ReturnType<typeof useRecords>
  templateId: string
}) {
  if (records.isLoading) return <FxSpinner label="Datensätze werden geladen …" />

  if (records.error) {
    return (
      <FxAlert tone="danger" title="Die Datensätze konnten nicht geladen werden">
        <p>Möglicherweise ist die Verbindung kurz weg. Versuchen Sie es noch einmal.</p>
        <FxButton variant="outlined" className="mt-4" onClick={records.refetch}>
          Erneut versuchen
        </FxButton>
      </FxAlert>
    )
  }

  if (records.columns.length === 0) {
    return (
      <FxAlert tone="info" title="Diese Vorlage hat noch keine Felder">
        <p>
          Legen Sie im Editor fest, welche Felder ausgelesen werden sollen — danach entsteht hier
          pro Dokument ein Datensatz.
        </p>
        <Link className="text-primary-text" to={`/templates/${templateId}/vorlage`}>
          Zur Vorlage
        </Link>
      </FxAlert>
    )
  }

  if (records.loadedCount === 0) {
    return (
      <FxAlert
        tone="info"
        title={
          records.needsReviewOnly
            ? 'Keine unklaren Datensätze'
            : 'Noch keine ausgewerteten Dokumente'
        }
      >
        <p>
          {records.needsReviewOnly
            ? 'Alle ausgewerteten Dokumente sind ohne Beanstandung.'
            : 'Sobald ein Dokument ausgewertet ist, steht sein Datensatz hier.'}
        </p>
        <Link className="text-primary-text" to={`/templates/${templateId}`}>
          Zu den Dokumenten
        </Link>
      </FxAlert>
    )
  }

  const hidden = records.loadedCount - records.rows.length

  return (
    <>
      <RecordsTable
        templateId={templateId}
        columns={records.columns}
        rows={records.rows}
        showFallbackMarker={records.arm !== DELIVERED}
        caption="Ausgelesene Werte je Dokument, eine Spalte je Feld der Vorlage"
      />

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <p className="m-0 text-small text-text-muted">
          {records.rows.length} von {records.loadedCount} geladenen Datensätzen
          {hidden > 0 ? ` (${hidden} durch die Suche ausgeblendet)` : ''}
          {records.totalRecords != null && records.loadedCount < records.totalRecords
            ? ` · ${records.totalRecords} insgesamt`
            : ''}
        </p>
        {records.hasMore && (
          <FxButton
            variant="tonal"
            disabled={records.isLoadingMore}
            onClick={records.loadMore}
          >
            {records.isLoadingMore ? 'Wird geladen …' : 'Weitere 50 laden'}
          </FxButton>
        )}
      </div>

      <p className="mt-4 text-small">
        Die Werte stammen aus der Texterkennung; geprüfte Korrekturen haben Vorrang. Rot markierte
        Zellen sind unklar und warten auf eine Prüfung.
      </p>
    </>
  )
}

export default RecordsTab
